import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Database, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-sky-100 bg-white px-3 py-2">
    <p className="text-2xl font-bold font-heading text-sky-900" data-testid={testId}>{value}</p>
    <p className="text-xs text-sky-700/70">{label}</p>
  </div>
);

const personLabel = (c) => `${c.client_number != null ? `#${c.client_number} ` : ""}${c.name || ""} ${c.last_name || ""}`.trim();

export const TripsMigrationCard = ({ api, axiosConfig, onDone }) => {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${api}/admin/trips-migration/preview`, axiosConfig);
      setSummary(res.data);
    } catch {
      setSummary(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${api}/admin/trips-migration/run`, {}, axiosConfig);
      toast.success(`Migración completa: ${res.data.clients_after} clientes · ${res.data.trips_after} viajes`);
      setOpen(false);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la migración");
    } finally {
      setRunning(false);
    }
  };

  if (!summary || summary.pending_clients === 0) return null;

  return (
    <>
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="trips-migration-banner">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-900">
            <strong>{summary.pending_clients} clientes</strong> todavía no pasaron a la nueva estructura de viajes.
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="trips-migration-open-button">
          Ver resumen de migración
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trips-migration-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl">Resumen de la migración a viajes</DialogTitle>
            <DialogDescription>
              Cada cliente pasa a tener un viaje con sus vouchers y su recordatorio. No se borra ningún dato y los links ya enviados siguen funcionando.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Clientes antes" value={summary.clients_before} testId="mig-clients-before" />
            <Stat label="Clientes después" value={summary.clients_after} testId="mig-clients-after" />
            <Stat label="Viajes después" value={summary.trips_after} testId="mig-trips-after" />
            <Stat label="Viajes activos" value={summary.active_trips} testId="mig-active" />
            <Stat label="Viajes a historial" value={summary.history_trips} testId="mig-history" />
            <Stat label="Vouchers asignados" value={summary.vouchers_to_assign} testId="mig-vouchers" />
          </div>
          <div className="text-sm text-sky-800">
            <p><strong>{summary.reminders_to_move}</strong> recordatorios configurados pasan a su viaje.</p>
            <p className="mt-1"><strong>{summary.merged_clients}</strong> clientes repetidos (mismo email o teléfono) se unifican.</p>
          </div>
          {summary.merge_groups.length > 0 && (
            <div className="border border-sky-100 rounded-lg divide-y divide-sky-100 max-h-56 overflow-y-auto" data-testid="mig-merge-groups">
              {summary.merge_groups.map((g) => (
                <div key={g.primary.id} className="px-3 py-2 text-sm">
                  <p className="font-semibold text-sky-900">{personLabel(g.primary)} <span className="font-normal text-sky-600">· {g.primary.email}</span></p>
                  <p className="text-xs text-sky-700/80">
                    Se le suman: {g.duplicates.map((d) => `${personLabel(d)} (${d.email})`).join(", ")}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-sky-200 text-sky-700" data-testid="mig-cancel-button">Cancelar</Button>
            <Button onClick={run} disabled={running} className="btn-sky text-white" data-testid="mig-confirm-button">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirmar migración
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
